/**
 * Copyright (c) 2020-2021 Perficient, Inc.
 * Unauthorized copying or distribution of this file prohibited without the express permission of Perficient.
 */

import React, { useState, useEffect } from "react";
import "./MetricItem.css";
import { Sparklines, SparklinesLine } from "react-sparklines";
import { secondsToHms, avg } from "../../utility";

const MAX_ITEMS_IN_CHART = 8;

/**
 * Reduces an array of numbers, to an array of provided min length
 * by averaging the original array in batches.
 * For example reduceToTimeTrend([1, 2, 3, 4, 5, 6], 2) => [2, 5, 6].
 * Average of 1, 2, 3 is 2, average of 4, 5, 6 is 5, and we append the last
 * value 6 to the end to make sure the latest trend is reflected.
 * @param {Array} array array to reduce
 * @param {Number} resultMinLength minimum length of the resulting array
 */
const reduceToTimeTrend = (array, resultMinLength) => {
	// if there aren't at least twice as many items in the array as the resultMinLength,
	// we return an array of the latest values the size of the resultMinLength, otherwise
	// the resulting array is smaller than the resultMinLength
	if (array.length < resultMinLength * 2) {
		return array.slice(-resultMinLength);
	}

	// we'll iterate the array in delta increments
	const delta = Math.floor(array.length / resultMinLength);

	const newArray = [];

	// in batches of size delta, go through the array and average the batches
	for (let index = 0; index < array.length; index = index + delta) {
		newArray.push(avg(array.slice(index, index + delta)));
	}

	// to make sure the latest trend is reflected in the result
	// append the latest value to the resulting array
	newArray.push(array[array.length - 1]);

	return newArray;
};

/**
 * Displays a metric and a data trend chart
 * @param {Object} params
 * - metric: data about the metric for this item
 * - trendLength: max number of metric data values to use to calculate the data trend
 */
const MetricItem = ({ metric, trendLength }) => {
	const value = metric.data ? metric.data.value : null;
	const unit = metric.data ? metric.data.unit : "";

	const [chartData, setChartData] = useState([]);

	useEffect(() => {
		if (metric.data) {
			const value = metric.data.value ?? 0;
			setChartData((c) => [...c.slice(-trendLength), value]);
		}
	}, [metric, trendLength]);

	return (
		<div className="metric-item-wrapper">
			<div className="metric-item-chart">
				<Sparklines data={reduceToTimeTrend(chartData, MAX_ITEMS_IN_CHART)} height={42}>
					<SparklinesLine color="#58595b" />
				</Sparklines>
			</div>
			<div className="metric-item-content">
				<div className="metric-item-label">
					<span className="mr-1">
						<i className="fas fa-info-circle" title={metric.description}></i>
					</span>
					<span title={metric.label}>{metric.label}</span>
				</div>
				<div className="metric-item-value-wrapper">
					<span className="metric-item-value">{_valueDisplay(metric.id, unit, value)}</span>
				</div>
			</div>
		</div>
	);
};

/**
 * Default values to display if the metric is not available
 */
const METRIC_DISPLAY_DEFAULTS = {
	QUEUED_TIME: "N/A",
	DEFAULT: "0",
};

const _valueDisplay = (metricId, unit, value) => {
	const metricValue = value ? value : METRIC_DISPLAY_DEFAULTS[metricId] || METRIC_DISPLAY_DEFAULTS.DEFAULT;

	const labels = {
		COUNT: metricValue,
		SECONDS: typeof metricValue === "string" ? metricValue : secondsToHms(metricValue),
		PERCENT: `${metricValue} %`,
	};

	return labels[unit] || metricValue;
};

export default MetricItem;
